/**
 * ScatterNode - Generates position + rotation arrays for spatial distribution
 *
 * Produces posX/posY/posZ and rotX/rotY/rotZ arrays from configurable
 * distribution patterns (grid, random, hex, radial, line). Connect outputs
 * to a Repeater to spatially distribute instances.
 *
 * `count` is the universal control — all modes generate exactly `count` points.
 * For grid/hex, `cols` sets the column width and rows are derived from count.
 */

import { BaseNode } from '@nodes/BaseNode';
import { Domain } from '@nodes/Endpoint';
import { NodeDef } from '@runtime/IRuntime';
import { Payload } from '@graph/Payload';
import { params, internals } from '@/nodes/TypedEndpoints';
import { seededRandom } from '../../randomUtils';

/** Simple 2D value noise using seeded hash with smooth interpolation */
function valueNoise2D(x: number, z: number, seed: number): number {
  const hash = (ix: number, iz: number) => {
    let h = (ix * 374761393 + iz * 668265263 + seed * 1274126177) | 0;
    h = ((h ^ (h >> 13)) * 1274126177) | 0;
    return ((h ^ (h >> 16)) >>> 0) / 0xffffffff;
  };

  const ix = Math.floor(x);
  const iz = Math.floor(z);
  const fx = x - ix;
  const fz = z - iz;

  // Smoothstep
  const sx = fx * fx * (3 - 2 * fx);
  const sz = fz * fz * (3 - 2 * fz);

  const v00 = hash(ix, iz);
  const v10 = hash(ix + 1, iz);
  const v01 = hash(ix, iz + 1);
  const v11 = hash(ix + 1, iz + 1);

  const a = v00 + sx * (v10 - v00);
  const b = v01 + sx * (v11 - v01);
  return (a + sz * (b - a)) * 2 - 1; // range [-1, 1]
}

// ─────────────────────────────────────────────────────────────
// Distribution generators
// ─────────────────────────────────────────────────────────────

type Points = { xs: number[]; zs: number[] };

/** Generate `count` points in a grid, `cols` wide, filling row by row */
function generateGrid(count: number, cols: number, spacingX: number, spacingZ: number): Points {
  const xs: number[] = [];
  const zs: number[] = [];
  const rows = Math.ceil(count / cols);
  const offsetX = ((cols - 1) * spacingX) / 2;
  const offsetZ = ((rows - 1) * spacingZ) / 2;

  for (let i = 0; i < count; i++) {
    const r = Math.floor(i / cols);
    const c = i % cols;
    xs.push(c * spacingX - offsetX);
    zs.push(r * spacingZ - offsetZ);
  }
  return { xs, zs };
}

/** Generate `count` points in a hex grid, `cols` wide, odd rows offset by half spacingX */
function generateHex(count: number, cols: number, spacingX: number, spacingZ: number): Points {
  const xs: number[] = [];
  const zs: number[] = [];
  const rows = Math.ceil(count / cols);
  const offsetX = ((cols - 1) * spacingX) / 2;
  const offsetZ = ((rows - 1) * spacingZ) / 2;

  for (let i = 0; i < count; i++) {
    const r = Math.floor(i / cols);
    const c = i % cols;
    const rowOffset = r % 2 === 1 ? spacingX / 2 : 0;
    xs.push(c * spacingX - offsetX + rowOffset);
    zs.push(r * spacingZ - offsetZ);
  }
  return { xs, zs };
}

function generateRandom(count: number, spread: number, seed: number): Points {
  const rng = seededRandom(seed);
  const xs: number[] = [];
  const zs: number[] = [];
  const half = spread / 2;

  for (let i = 0; i < count; i++) {
    xs.push(rng() * spread - half);
    zs.push(rng() * spread - half);
  }
  return { xs, zs };
}

/** Distribute `count` total points across `rings` concentric circles */
function generateRadial(count: number, rings: number, radius: number): Points {
  const xs: number[] = [];
  const zs: number[] = [];
  const perRing = Math.floor(count / rings);
  let remaining = count - perRing * rings;

  for (let ring = 1; ring <= rings; ring++) {
    const r = (ring / rings) * radius;
    const n = perRing + (remaining > 0 ? 1 : 0);
    if (remaining > 0) remaining--;

    for (let i = 0; i < n; i++) {
      const angle = (i / n) * Math.PI * 2;
      xs.push(Math.cos(angle) * r);
      zs.push(Math.sin(angle) * r);
    }
  }
  return { xs, zs };
}

function generateLine(count: number, spacingZ: number): Points {
  const xs: number[] = [];
  const zs: number[] = [];
  const offset = ((count - 1) * spacingZ) / 2;

  for (let i = 0; i < count; i++) {
    xs.push(0);
    zs.push(i * spacingZ - offset);
  }
  return { xs, zs };
}

// ─────────────────────────────────────────────────────────────
// Rotation generators
// ─────────────────────────────────────────────────────────────

function computeRotations(
  mode: string,
  xs: number[],
  zs: number[]
): { rotXs: number[]; rotYs: number[]; rotZs: number[] } {
  const n = xs.length;
  const rotXs = new Array(n).fill(0);
  const rotYs = new Array(n).fill(0);
  const rotZs = new Array(n).fill(0);

  if (mode === 'radial') {
    // Face outward from center
    for (let i = 0; i < n; i++) {
      rotYs[i] = Math.atan2(xs[i], zs[i]);
    }
  }

  return { rotXs, rotYs, rotZs };
}

// ─────────────────────────────────────────────────────────────
// Endpoints
// ─────────────────────────────────────────────────────────────

const scatterEndpoints = {
  ...params({
    mode: { dataType: 'string', value: 'grid', options: ['grid', 'random', 'hex', 'radial', 'line'] },
    count: { value: 9, min: 1, max: 256, step: 1 },
    seed: { value: 42, min: 0, max: 9999, step: 1 },

    // Grid/hex
    cols: { value: 3, min: 1, max: 32, step: 1, group: 'Layout' },
    spacingX: { value: 10, min: 0.1, max: 200, step: 0.1, group: 'Layout' },
    spacingZ: { value: 10, min: 0.1, max: 200, step: 0.1, group: 'Layout' },

    // Random
    spread: { value: 50, min: 1, max: 500, step: 1, group: 'Layout' },

    // Radial
    radius: { value: 20, min: 1, max: 200, step: 0.5, group: 'Layout' },
    rings: { value: 3, min: 1, max: 16, step: 1, group: 'Layout' },

    // Height
    heightMode: { dataType: 'string', value: 'flat', options: ['flat', 'sine', 'noise'], group: 'Height' },
    heightScale: { value: 0, min: 0, max: 50, step: 0.1, group: 'Height' },
    heightFreq: { value: 0.1, min: 0.01, max: 2, step: 0.01, group: 'Height' },
  }),
  ...internals({
    posX: { dataType: 'array', isOutput: true, label: 'X' },
    posY: { dataType: 'array', isOutput: true, label: 'Y' },
    posZ: { dataType: 'array', isOutput: true, label: 'Z' },
    rotX: { dataType: 'array', isOutput: true, label: 'Rot X' },
    rotY: { dataType: 'array', isOutput: true, label: 'Rot Y' },
    rotZ: { dataType: 'array', isOutput: true, label: 'Rot Z' },
  }),
};

// ─────────────────────────────────────────────────────────────
// Node
// ─────────────────────────────────────────────────────────────

class ScatterNode extends BaseNode<typeof scatterEndpoints> {

  initialize(): void {
    this.compute();
  }

  onParamChanged(_payload: Payload, _endpointId: string): void {
    this.compute();
  }

  private compute(): void {
    const mode = this.params.mode.value as string;
    const cols = this.params.cols.value;
    const spacingX = this.params.spacingX.value;
    const spacingZ = this.params.spacingZ.value;
    const count = this.params.count.value;
    const seed = this.params.seed.value;
    const spread = this.params.spread.value;
    const radius = this.params.radius.value;
    const rings = this.params.rings.value;

    // Generate X/Z positions — all modes use `count` as total
    let points: Points;
    switch (mode) {
      case 'grid':    points = generateGrid(count, cols, spacingX, spacingZ); break;
      case 'hex':     points = generateHex(count, cols, spacingX, spacingZ); break;
      case 'random':  points = generateRandom(count, spread, seed); break;
      case 'radial':  points = generateRadial(count, rings, radius); break;
      case 'line':    points = generateLine(count, spacingZ); break;
      default:        points = generateGrid(count, cols, spacingX, spacingZ); break;
    }

    const { xs, zs } = points;

    // Generate Y positions from height mode
    const heightMode = this.params.heightMode.value as string;
    const heightScale = this.params.heightScale.value;
    const heightFreq = this.params.heightFreq.value;
    const ys = this.computeHeights(xs, zs, heightMode, heightScale, heightFreq, seed);

    // Generate rotations
    const { rotXs, rotYs, rotZs } = computeRotations(mode, xs, zs);

    // Emit outputs
    this.internals.posX.set(xs);
    this.internals.posY.set(ys);
    this.internals.posZ.set(zs);
    this.internals.rotX.set(rotXs);
    this.internals.rotY.set(rotYs);
    this.internals.rotZ.set(rotZs);
  }

  private computeHeights(
    xs: number[], zs: number[],
    mode: string, scale: number, freq: number, seed: number
  ): number[] {
    const n = xs.length;

    if (mode === 'flat' || scale === 0) {
      return new Array(n).fill(0);
    }

    const ys = new Array(n);
    if (mode === 'sine') {
      for (let i = 0; i < n; i++) {
        ys[i] = scale * Math.sin(xs[i] * freq) * Math.cos(zs[i] * freq);
      }
    } else {
      // noise
      for (let i = 0; i < n; i++) {
        ys[i] = scale * valueNoise2D(xs[i] * freq, zs[i] * freq, seed);
      }
    }
    return ys;
  }
}

// ─────────────────────────────────────────────────────────────
// Node Definition
// ─────────────────────────────────────────────────────────────

export const scatterNodeDef: NodeDef<ScatterNode> = {
  type: 'Scatter',
  info: 'Generates position and rotation arrays from configurable distributions (grid, random, hex, radial, line).',
  domain: Domain.Data,
  category: 'Layout',
  shortName: 'scatter',
  endpoints: scatterEndpoints,
  ctor: ScatterNode,
};
